import { sv } from "@dhx/ts-common/dom";
import { getCoordinates, pieLikeHandlers } from "../helpers/circle";
import {
	checkPositions,
	getDefaultColor,
	getSizesSVGText,
	roundToTwoNumAfterPoint,
	verticalCenteredText,
} from "../helpers/common";
import { SvgElement } from "../types";
import NoScaleSeria from "./NoScaleSeria";

export default class Donut extends NoScaleSeria {
	paint(width: number, height: number): SvgElement {
		const getPointAriaAttrs = (value, text, percent = 0) => ({
			role: "graphics-symbol",
			"aria-roledescription": "piece of donut",
			"aria-label": `${text || ""}, ${value} (${roundToTwoNumAfterPoint(percent)}%)`,
		});
		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.text || ""}`,
		});

		const { stroke, strokeWidth, useLines, subType } = this.config;
		const defaultStrokeWidth = !strokeWidth || strokeWidth < 1 ? 4 : strokeWidth > 15 ? 15 : strokeWidth;

		let radius;
		if (height > width) {
			radius = width / 2;
		} else {
			radius = height / 2;
		}
		let currentPercent = -0.25;
		let svg = [];
		const lines = [];
		const textPoints = [];

		const tooltipData = [];

		if (this._points.length > 1 && stroke) {
			svg.push(
				sv("circle", {
					cx: 0,
					cy: 0,
					r: radius - 0.5,
					fill: stroke,
				})
			);
		}

		this._points.forEach((item, index) => {
			const link = [];
			const [percent, value, id, text, color] = item;
			const err = percent === 0 || percent === 1 ? -0.000001 : 0;
			const [startX, startY] = getCoordinates(
				currentPercent,
				radius,
				radius,
				this._points.length > 1 && stroke ? defaultStrokeWidth / 2 : null
			);
			const avPercent = currentPercent + percent / 2;
			currentPercent += percent + err;
			const [endX, endY] = getCoordinates(
				currentPercent,
				radius,
				radius,
				this._points.length > 1 && stroke ? -defaultStrokeWidth / 2 : null
			);
			const largeArcFlag = percent > 0.5 ? 1 : 0;
			const middleLine = getCoordinates(avPercent, radius, radius);
			const isRight = avPercent > -0.25 && avPercent < 0.25;
			const isUp = avPercent > 0.5 || avPercent < 0;
			let lineLength = avPercent < 0.25 ? 5 : -5;
			const [startPart, endPart] = [5, 30];
			const [linkStart, linkEnd] = [
				getCoordinates(avPercent, radius + startPart, radius + startPart),
				getCoordinates(avPercent, radius + endPart, radius + endPart),
			];

			switch (this.config.subType) {
				case "basic": {
					const className = isRight ? "donut-value-title start-text" : "donut-value-title end-text";
					const textPadding = 10;
					const dy = isUp ? -textPadding * 2 : textPadding;
					const linkStart = getCoordinates(avPercent, radius + 10, radius + 10);
					const className2 = isRight ? "donut-value start-text" : "donut-value end-text";

					const currentText = {
						text1: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							width: 0,
							height: 0,
							class: "",
						},
						text2: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							width: 0,
							height: 0,
							class: "",
						},
						changeSector: false,
						line: lineLength,
						right: isRight,
						dy: dy,
					};

					const text1 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							class: className,
							...getPointAriaAttrs(value, text, percent),
							tabindex: 0,
						},
						[verticalCenteredText(text.toString())]
					);
					const text2 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							class: className2,
							"aria-hidden": "true",
						},
						[verticalCenteredText(value.toString())]
					);

					[currentText.text1.width, currentText.text1.height] = getSizesSVGText(text.toString(), {
						font: "normal 14px Roboto",
						lineHeight: 14,
					});
					[currentText.text2.width, currentText.text2.height] = getSizesSVGText(value.toString(), {
						font: "normal 12px Roboto",
						lineHeight: 12,
					});

					const textRadius = useLines ? radius + endPart : radius + 10;

					if (textPoints.length !== 0) {
						if (isRight) {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text1,
									previosText.text1,
									textRadius,
									textRadius,
									currentText
								);
								checkPositions(
									currentText.text1,
									previosText.text2,
									textRadius,
									textRadius,
									currentText
								);
							});
							if (currentText.text1.class) currentText.text2.class = currentText.text1.class;
						} else {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text2,
									previosText.text2,
									textRadius,
									textRadius,
									currentText
								);
								checkPositions(
									currentText.text2,
									previosText.text1,
									textRadius,
									textRadius,
									currentText
								);
							});
							if (currentText.text2.class) currentText.text1.class = currentText.text2.class;
						}
						text1.attrs.x = currentText.text1.x;
						text1.attrs.y = currentText.text1.y;
						text2.attrs.x = currentText.text2.x;
						text2.attrs.y = currentText.text2.y;
						if (currentText.text1.class || currentText.text2.class) {
							text1.attrs.class = currentText.text1.class;
							text2.attrs.class = currentText.text2.class;
						}
						lineLength = currentText.line;

						if (useLines) {
							linkEnd[0] = currentText.text1.x;
							linkEnd[1] = currentText.text1.y - dy;
						} else {
							linkStart[0] = currentText.text1.x;
							linkStart[1] = currentText.text1.y - dy;
						}
					}

					textPoints.push(currentText);

					const text3 = sv(
						"text",
						{
							x: (middleLine[0] * 7) / 9,
							y: (middleLine[1] * 7) / 9,
							class: "pie-inner-value",
							...getPointAriaAttrs(value, text, percent),
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);
					link.push(
						sv(
							"g",
							{
								id: `${id}-text`,
								class: "chart donut",
							},
							[text1, text2]
						)
					);
					link.push(text3);
					break;
				}
				case "valueOnly": {
					const valueText = sv(
						"text",
						{
							x: (middleLine[0] * 7) / 9,
							y: (middleLine[1] * 7) / 9,
							class: "pie-inner-value",
							...getPointAriaAttrs(value, text, percent),
							tabindex: 0,
						},
						[verticalCenteredText(`${value}`)]
					);
					link.push(valueText);
					break;
				}
				case "percentOnly": {
					const percentText = sv(
						"text",
						{
							x: (middleLine[0] * 7) / 9,
							y: (middleLine[1] * 7) / 9,
							class: "pie-inner-value",
							...getPointAriaAttrs(value, text, percent),
							tabindex: 0,
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);
					link.push(percentText);
					break;
				}
			}

			if (useLines && subType === "basic") {
				const dy = isUp ? 3 : 0;
				link.push(
					sv("path", {
						d: `M${linkStart[0]} ${linkStart[1]} L${linkEnd[0]}
							${linkEnd[1] + dy} h ${lineLength}`,
						id: `${id}-connector`,
						class: "pie-value-connector chart donut",
					})
				);
			}

			let centerX = 0,
				centerY = 0;
			const pseudoRadius = defaultStrokeWidth / (2 * Math.sin(Math.PI / this._points.length));
			if (this._points.length > 1 && stroke) {
				[centerX, centerY] = getCoordinates(avPercent, pseudoRadius, pseudoRadius);
			}

			const d = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
				L ${0 + centerX} ${0 + centerY}`;
			const [shiftX, shiftY] = getCoordinates(avPercent, 4, 4);
			const currentSector = sv("path", {
				d,
				_key: id,
				fill: color || getDefaultColor(index),
				class: "chart donut",
				onclick: [this._handlers.onclick, item[1], item[2]],

				// eslint-disable-next-line @typescript-eslint/unbound-method
				onmouseout: [pieLikeHandlers.onmouseout],
				// eslint-disable-next-line @typescript-eslint/unbound-method
				onmouseover: [pieLikeHandlers.onmouseover, shiftX, shiftY],
				role: "presentation",
			});

			link.unshift(currentSector);
			svg.push(sv("g", { id: id }, link));

			if (this._points.length === 1) {
				tooltipData.push([width / 2, height / 2]);
			} else {
				tooltipData.push([middleLine[0] * 0.8 + width / 2, middleLine[1] * 0.8 + height / 2]);
			}
		});

		this._center = [width / 2, height / 2];
		this._tooltipData = tooltipData;

		svg = svg.concat(lines);
		svg.push(
			sv("circle", {
				cx: 0,
				cy: 0,
				r: (radius * 5) / 9,
				fill: "#FFFFFF",
				role: "presentation",
			})
		);
		return sv(
			"g",
			{
				transform: `translate(${width / 2}, ${height / 2})`,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
			},
			svg
		);
	}
}
